// Shared pure helpers for the group-assignment draft model. The lesson inline
// editor (LessonGroupsView) uses these; they mirror the inline versions in
// ClassroomAssignmentView (Differentiation) so the two flows stay consistent.

import { normalizeGrade } from "utils/standards";

export const compareCode = (a, b) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

// Stable signature of a draft's assignment state, used for dirty detection.
export const signature = (bundles, unassigned, sessionLength) => JSON.stringify({
  s: sessionLength,
  u: [...unassigned].sort(compareCode),
  b: bundles
    .map((b) => [
      b.assignment_id,
      [...b.assignments]
        .map((a) => `${a.group_code}|${a.standard_code}|${a.track_type}`)
        .sort(),
    ])
    .sort((x, y) => x[0].localeCompare(y[0])),
});

// Lesson track_type is singular "extension"; AssignmentGroupCard's TRACK_META keys
// are plural and silently fall back to Core styling for unknown keys, so map for
// display, and map back when persisting to the lesson group-assignments endpoint.
export const toDisplayTrack = (t) => (t === "extension" ? "extensions" : t);
export const toLessonTrack = (t) => (t === "extensions" ? "extension" : t);

// Assignment bundles carry `last_updated_at`; quick groups carry `last_updated`.
export const timestamp = (b) => b?.last_updated_at || b?.last_updated || "";

// The classroom's most-recently-touched entry — bundle or quick group — by
// timestamp (ISO strings compare chronologically). Falls back to the last entry
// when none carries one: the endpoint orders by created_at, so the tail is the
// newest. Null when there are no entries.
export const mostRecentBundle = (bundles = []) => {
  const byTimestamp = bundles.reduce(
    (latest, b) => (timestamp(b) && (!latest || timestamp(b) > timestamp(latest)) ? b : latest),
    null,
  );
  return byTimestamp || bundles[bundles.length - 1] || null;
};

// What the class last worked on, normalized across every entry shape: lesson
// bundles, v2 standard bundles, the v1 draft shape (primary_standard_code) and
// quick groups (flat standard_code) — a quick group is the last thing worked on
// as much as a lesson is, so it gets a vote. One record backs all three create
// flows — the lesson picker, the standard picker, and the quick-group grade —
// so they can't drift apart. Null when there's nothing to derive, which is what
// makes it safe to persist (see useLastFocus).
export const lastFocusRecord = (bundles = []) => {
  const b = mostRecentBundle(bundles);
  if (!b) return null;
  // Carried so the memory can only ever move forward: archiving removes rows, so
  // the derive it leaves behind is older, and an older derive never overwrites.
  const updatedAt = timestamp(b);
  if (b.lesson?.id) {
    return {
      lessonId: b.lesson.id,
      standardCode: null,
      // The bundle carries no course id, so grade is how the picker resolves it.
      grade: normalizeGrade(b.lesson.course_grade_level || ""),
      updatedAt,
    };
  }
  const code = b.standard?.standard_code || b.primary_standard_code || b.standard_code || "";
  if (!code) return null;
  return {
    lessonId: null,
    standardCode: code,
    // The code's own prefix is the grade the tree offers — "HSN.RN.A.1" opens
    // HSN, not a bare "HS", which is no grade at all.
    grade: code.split(".")[0],
    updatedAt,
  };
};

// Extra LessonsStandards URL params to open the picker on that record: a lesson
// record opens that lesson; a standard record opens Standard mode at its grade,
// on that standard — the grade alone leaves the detail panel empty, so the
// standard flow would lose its default where the lesson flow keeps one.
// Empty without a record, so the picker keeps its teacher-grade / first-lesson
// default.
export const focusPickerParams = (record) => {
  if (!record) return {};
  if (record.lessonId) {
    return record.grade ? { lessonId: record.lessonId, grade: record.grade } : { lessonId: record.lessonId };
  }
  return {
    mode: "standard",
    grade: record.grade,
    ...(record.standardCode ? { standard: record.standardCode } : {}),
  };
};

// First run of digits in a free-form ordinal label ("Unit 3" -> "3",
// "Lesson 2" -> "2"). Returns "" when there's no digit (e.g. grade "K").
export const numToken = (s) => (String(s ?? "").match(/\d+/) || [""])[0];

// Sort options for the dashboard listings — Quick Groups and Lessons in both the
// differentiation and lesson-picker flows. "Last Updated" is the default: newest
// on top.
export const SORT_OPTIONS = [
  { value: "recent", label: "Last Updated" },
  { value: "sequence", label: "Lesson Sequence" },
];

// The standards flow groups by standard rather than by lesson, so the same
// ordering reads as "Standards" there. Derived from SORT_OPTIONS — only the
// label differs, the values (and so the sorting) are identical.
export const STANDARD_SORT_OPTIONS = SORT_OPTIONS.map((o) =>
  (o.value === "sequence" ? { ...o, label: "Standards" } : o));

// Kindergarten sorts ahead of grade 1; a grade we can't read sorts last so it
// never displaces the sequence of the ones we can.
const gradeNum = (s) => {
  const digits = numToken(s);
  if (digits !== "") return Number(digits);
  return /\bk\b/i.test(String(s ?? "")) ? 0 : 99;
};

// Curriculum position as a comparable tuple: [grade, kind, unit, lesson, code].
// Lesson bundles order by their course/unit/lesson ordinals; standard-backed
// entries (which carry no ordinals) order by standard code within their grade,
// after the lessons of that grade. Handles the v2 bundle shape (lesson /
// standard), the v1 draft shape (primary_standard_code) and quick groups
// (standard_code).
const sequenceKey = (b) => {
  const lesson = b?.lesson;
  if (lesson) {
    return [
      gradeNum(lesson.course_grade_level || lesson.course_name),
      0,
      Number(numToken(lesson.unit_ordinal_name) || 0),
      Number(numToken(lesson.ordinal_name) || 0),
      "",
    ];
  }
  const code = b?.standard?.standard_code || b?.primary_standard_code || b?.standard_code || "";
  return [gradeNum(code.split(".")[0]), 1, 0, 0, code];
};

const bySequence = (a, b) => {
  const x = sequenceKey(a);
  const y = sequenceKey(b);
  for (let i = 0; i < 4; i += 1) {
    if (x[i] !== y[i]) return x[i] - y[i];
  }
  return x[4].localeCompare(y[4], undefined, { numeric: true, sensitivity: "base" });
};

// Newest first. Entries without a timestamp keep their server order at the end;
// if NO entry carries one, the server's created_at order is reversed instead
// (its tail is the newest — the same assumption `mostRecentBundle` makes).
const byLastUpdated = (entries) => {
  if (!entries.some(timestamp)) return [...entries].reverse();
  return [...entries].sort((a, b) => timestamp(b).localeCompare(timestamp(a)));
};

// Order a listing's entries — assignment bundles or quick groups — by the shared
// Sort By options. Pure; returns a new array (Array.prototype.sort is stable, so
// equal keys keep server order).
export const sortBundles = (entries = [], sortBy = "recent") =>
  (sortBy === "sequence" ? [...entries].sort(bySequence) : byLastUpdated(entries));

// Count a bundle's assignments into the distribution-modal keys
// ({ core, prerequisites, extensions }), tolerating singular/plural track forms.
export const countDistribution = (assignments) => {
  const dist = { core: 0, prerequisites: 0, extensions: 0 };
  (assignments || []).forEach((a) => {
    const t = a.track_type;
    if (t === "core") dist.core += 1;
    else if (t === "prerequisite" || t === "prerequisites") dist.prerequisites += 1;
    else if (t === "extension" || t === "extensions") dist.extensions += 1;
  });
  return dist;
};
